import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import type { Profile } from "../types";
import EditorMinisite from "./EditorMinisite";
import PresencaEmIa from "./PresencaEmIa";

type Props = { profile: Profile };

/**
 * O gerador de QR é carregado sob demanda de /qrcode.min.js (servido pelo
 * próprio app, sem CDN). Fica fora do bundle porque só esta aba usa.
 */
declare global {
  interface Window {
    qrcode?: (tipo: number, correcao: string) => {
      addData: (s: string) => void;
      make: () => void;
      createDataURL: (celula?: number, margem?: number) => string;
    };
  }
}

let carregandoQr: Promise<void> | null = null;
function carregarQrcode(): Promise<void> {
  if (window.qrcode) return Promise.resolve();
  if (carregandoQr) return carregandoQr;
  carregandoQr = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "/qrcode.min.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("falha ao carregar o gerador de QR"));
    document.head.appendChild(s);
  });
  return carregandoQr;
}

/**
 * Correção 'H' (30%), não 'M': o cartão impresso pode levar logo sobre o QR e
 * vai ser lido de longe/borrado. Com 'M' alguns leitores falham nesse cenário.
 */
function gerarQrDataUrl(texto: string): string | null {
  if (!window.qrcode) return null;
  const qr = window.qrcode(0, "H");
  qr.addData(texto);
  qr.make();
  return qr.createDataURL(6, 4);
}

// ---- catálogos servidos de /presenca-digital/ (WebP, 6MB no total) ----
type Moldura = { id: string; arquivo: string; tema: string; estilo: string };
type Arte = { id: string; segmento: string; estilo: string; arquivo: string };

const BASE_ASSETS = "/presenca-digital";

const NOMES_SEGMENTOS: Record<string, string> = {
  "automotivo": "Automotivo",
  "beleza-estetica": "Beleza & Estética",
  "casa-construcao": "Casa & Construção",
  "celular-assistencia": "Celular & Assistência",
  "distribuidora-corporativo": "Distribuidora & Corporativo",
  "food": "Alimentação",
  "joalheria": "Joalheria",
  "moda-varejo": "Moda & Varejo",
  "pet": "Pet",
  "saude-clinicas": "Saúde & Clínicas",
  "seguranca-eletronica": "Segurança Eletrônica",
  "tecnologia-internet": "Tecnologia & Internet",
};

export function PresencaDigitalTab({ profile }: Props) {
  const [tela, setTela] = useState<"hub" | "cartao" | "minisite">("hub");
  const [qr, setQr] = useState<string | null>(null);
  const [erroQr, setErroQr] = useState<string | null>(null);

  const linkAvaliacao = profile.reviewUrl || "";

  useEffect(() => {
    if (!linkAvaliacao) return;
    let cancelado = false;
    carregarQrcode()
      .then(() => { if (!cancelado) setQr(gerarQrDataUrl(linkAvaliacao)); })
      .catch((e: Error) => { if (!cancelado) setErroQr(e.message); });
    return () => { cancelado = true; };
  }, [linkAvaliacao]);

  if (tela === "cartao") {
    return (
      <EditorCartao
        profile={profile}
        qr={qr}
        erroQr={erroQr}
        linkAvaliacao={linkAvaliacao}
        onVoltar={() => setTela("hub")}
      />
    );
  }

  if (tela === "minisite") {
    return <EditorMinisite profile={profile} onVoltar={() => setTela("hub")} />;
  }

  return (
    <Shell>
      <Cabecalho style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <Titulo>Presença digital</Titulo>
          <Legenda>
            Mini-site gerado do perfil e cartão de avaliação com QR. Tudo sai dos dados já cadastrados; nada precisa ser redigitado.
          </Legenda>
        </div>
        <Seg role="tablist" aria-label="Ferramentas de presença digital">
          <button type="button" role="tab" aria-selected={false} onClick={() => setTela("minisite")}>
            <span className="material-icons">language</span>Mini-site
          </button>
          <button type="button" role="tab" aria-selected={false} disabled={!linkAvaliacao} onClick={() => linkAvaliacao && setTela("cartao")}>
            <span className="material-icons">qr_code_2</span>Cartão de avaliação
          </button>
        </Seg>
      </Cabecalho>

      <Grade>
        {/* ---------- 1. CARTÃO DE AVALIAÇÃO ---------- */}
        <CardProduto onClick={() => linkAvaliacao && setTela("cartao")} $ativo={Boolean(linkAvaliacao)}>
          <CardTopo>
            <IconeQuadrado $bg="#FFF7E0" $cor="#B06000">
              <span className="material-icons">qr_code_2</span>
            </IconeQuadrado>
            <div>
              <CardNome>Cartão de Avaliação</CardNome>
              <CardDesc>
                Cartão para imprimir e deixar no balcão. O cliente aponta a câmera
                e vai direto avaliar no Google.
              </CardDesc>
            </div>
          </CardTopo>

          <Mockup>
            <MockEstrelas>★★★★★</MockEstrelas>
            <MockFrase>Gostou do atendimento?</MockFrase>
            <MockQr>
              {qr ? <img src={qr} alt="" width={44} height={44} /> : (
                <span className="material-icons" style={{ fontSize: 38, color: "#1F1F1F" }}>qr_code_2</span>
              )}
            </MockQr>
            <MockSelo>
              <MockG>G</MockG>
              Avaliar no Google
            </MockSelo>
          </Mockup>

          <Tags>
            <Tag>72 artes por segmento</Tag>
            <Tag>10 molduras Google</Tag>
            <Tag>Impressão A6</Tag>
          </Tags>

          {linkAvaliacao ? (
            <Cta><span className="material-icons" style={{ fontSize: 18 }}>add</span>Criar cartão</Cta>
          ) : (
            <AvisoCard>
              Este perfil ainda não tem o link de avaliação do Google — ele vem na
              sincronização. Volte após a próxima sincronização.
            </AvisoCard>
          )}
        </CardProduto>

        {/* ---------- 2. MINI-SITE ---------- */}
        <CardProduto $ativo onClick={() => setTela("minisite")}>
          <CardTopo>
            <IconeQuadrado $bg="#EAF1FB" $cor="#0B4FA8">
              <span className="material-icons">language</span>
            </IconeQuadrado>
            <div>
              <CardNome>Mini-site</CardNome>
              <CardDesc>
                Página do negócio para compartilhar em bio, QR e campanhas —
                gerada dos dados deste perfil.
              </CardDesc>
            </div>
          </CardTopo>

          <MockupClaro>
            <MockJanela>
              <MockBolinhas><i /><i /><i /></MockBolinhas>
              <MockLinha style={{ width: "62%" }} />
              <MockLinha style={{ width: "84%" }} />
              <MockLinha style={{ width: "48%" }} />
              <MockBotaoFake />
            </MockJanela>
          </MockupClaro>

          <Tags>
            <Tag>Link para bio</Tag>
            <Tag>Endereço próprio (.georanking.site)</Tag>
          </Tags>

          <Cta>
            <span className="material-icons" style={{ fontSize: 18 }}>settings</span>
            Configurar mini-site
          </Cta>
        </CardProduto>
      </Grade>

      {/* ---------- 3. PRESENÇA EM IA (acompanhamento) ---------- */}
      <PresencaEmIa profileId={profile.id} />
    </Shell>
  );
}

// ============================ EDITOR DO CARTÃO ============================
// Fatia v1: preview + escolher moldura/arte + textos + QR + imprimir.
// Arraste, undo/redo, paleta da logo e toolbar ficam para v2 — são
// refinamento, não produto.

type EditorProps = {
  profile: Profile;
  qr: string | null;
  erroQr: string | null;
  linkAvaliacao: string;
  onVoltar: () => void;
};

function EditorCartao({ profile, qr, erroQr, linkAvaliacao, onVoltar }: EditorProps) {
  const [molduras, setMolduras] = useState<Moldura[]>([]);
  const [artes, setArtes] = useState<Arte[]>([]);
  const [erroAssets, setErroAssets] = useState(false);

  const [aba, setAba] = useState<"molduras" | "artes">("molduras");
  const [segmento, setSegmento] = useState<string>("automotivo");
  const [fundo, setFundo] = useState<string>("");
  const [fundoEscuro, setFundoEscuro] = useState(false);

  const [headline, setHeadline] = useState("Gostou do atendimento?");
  const [subtexto, setSubtexto] = useState("Aponte a câmera e avalie no Google");
  const [nomeNegocio, setNomeNegocio] = useState(profile.name || "");

  useEffect(() => {
    let cancelado = false;
    Promise.all([
      fetch(`${BASE_ASSETS}/molduras/catalog.json`).then((r) => r.json()),
      fetch(`${BASE_ASSETS}/biblioteca/catalog.json`).then((r) => r.json()),
    ])
      .then(([m, b]) => {
        if (cancelado) return;
        const ms: Moldura[] = m.molduras || [];
        setMolduras(ms);
        setArtes(b.bases || []);
        if (ms.length) {
          setFundo(`${BASE_ASSETS}/molduras/${ms[0].arquivo}`);
          setFundoEscuro(ms[0].tema === "escura");
        }
      })
      .catch(() => { if (!cancelado) setErroAssets(true); });
    return () => { cancelado = true; };
  }, []);

  const segmentos = useMemo(
    () => Array.from(new Set(artes.map((a) => a.segmento))),
    [artes],
  );
  const artesDoSegmento = useMemo(
    () => artes.filter((a) => a.segmento === segmento),
    [artes, segmento],
  );

  const imprimir = useCallback(() => { window.print(); }, []);

  const conteudoCartao = (
    <>
      <CartaoImg src={fundo} alt="" />
      <CartaoCentro $escuro={fundoEscuro}>
        <CartaoEstrelas>★★★★★</CartaoEstrelas>
        <CartaoHeadline>{headline}</CartaoHeadline>
        {subtexto && <CartaoSub>{subtexto}</CartaoSub>}
        <CartaoQrBox>
          {qr ? <img src={qr} alt="QR code da avaliação" /> : (
            <QrPendente>{erroQr ? "QR indisponível" : "gerando…"}</QrPendente>
          )}
        </CartaoQrBox>
        {nomeNegocio && <CartaoNome>{nomeNegocio}</CartaoNome>}
        <CartaoSelo>
          <MockG style={{ width: 15, height: 15, fontSize: 9 }}>G</MockG>
          Avaliar no Google
        </CartaoSelo>
      </CartaoCentro>
    </>
  );

  return (
    <Shell>
      <BarraEditor>
        <BotaoVoltar type="button" onClick={onVoltar}>
          <span className="material-icons" style={{ fontSize: 18 }}>arrow_back</span>
          Voltar
        </BotaoVoltar>
        <TituloEditor>Cartão de Avaliação</TituloEditor>
        <BotaoImprimir type="button" onClick={imprimir} disabled={!fundo || !qr}>
          <span className="material-icons" style={{ fontSize: 18 }}>print</span>
          Imprimir / Salvar PDF
        </BotaoImprimir>
      </BarraEditor>

      <EditorGrade>
        {/* ---------- preview ---------- */}
        <PreviewColuna>
          <CartaoPreview>
            {fundo ? conteudoCartao : (
              <QrPendente style={{ margin: "auto" }}>
                {erroAssets ? "Não foi possível carregar os modelos." : "Carregando modelos…"}
              </QrPendente>
            )}
          </CartaoPreview>
          <PreviewLegenda>
            A6 retrato (105 × 148 mm). Use “Imprimir / Salvar PDF” — o diálogo de
            impressão do navegador também salva em PDF.
          </PreviewLegenda>
        </PreviewColuna>

        {/* ---------- controles ---------- */}
        <Controles>
          <AbasFundo>
            <AbaFundo type="button" $ativa={aba === "molduras"} onClick={() => setAba("molduras")}>
              Molduras Google ({molduras.length})
            </AbaFundo>
            <AbaFundo type="button" $ativa={aba === "artes"} onClick={() => setAba("artes")}>
              Artes por segmento ({artes.length})
            </AbaFundo>
          </AbasFundo>

          {aba === "artes" && (
            <Campo>
              <Rotulo>Segmento</Rotulo>
              <Select value={segmento} onChange={(e) => setSegmento(e.target.value)}>
                {segmentos.map((s) => (
                  <option key={s} value={s}>{NOMES_SEGMENTOS[s] || s}</option>
                ))}
              </Select>
            </Campo>
          )}

          <Miniaturas>
            {(aba === "molduras"
              ? molduras.map((m) => ({
                  key: m.id,
                  src: `${BASE_ASSETS}/molduras/${m.arquivo}`,
                  escuro: m.tema === "escura",
                  titulo: m.estilo,
                }))
              : artesDoSegmento.map((a) => ({
                  key: a.id,
                  src: `${BASE_ASSETS}/biblioteca/${a.arquivo}`,
                  escuro: true,
                  titulo: a.estilo,
                }))
            ).map((item) => (
              <Miniatura
                key={item.key}
                type="button"
                title={item.titulo}
                $selecionada={fundo === item.src}
                onClick={() => { setFundo(item.src); setFundoEscuro(item.escuro); }}
              >
                <img src={item.src} alt={item.titulo} loading="lazy" />
              </Miniatura>
            ))}
          </Miniaturas>

          <Campo>
            <Rotulo>Frase principal</Rotulo>
            <Entrada value={headline} maxLength={40} onChange={(e) => setHeadline(e.target.value)} />
          </Campo>
          <Campo>
            <Rotulo>Frase de apoio</Rotulo>
            <Entrada value={subtexto} maxLength={60} onChange={(e) => setSubtexto(e.target.value)} />
          </Campo>
          <Campo>
            <Rotulo>Nome no cartão</Rotulo>
            <Entrada value={nomeNegocio} maxLength={48} onChange={(e) => setNomeNegocio(e.target.value)} />
          </Campo>

          <LinkInfo>
            <Rotulo>O QR abre</Rotulo>
            <LinkTexto>{linkAvaliacao}</LinkTexto>
          </LinkInfo>
        </Controles>
      </EditorGrade>

      {/* Área de impressão: vive no <body> (portal) para escapar do modal.
          Invisível na tela; no print, esconde todo o resto e imprime só o
          cartão em A6 — texto sai vetorial, sem rasterizar. */}
      {createPortal(
        <div id="gr-print-root">
          <style>{`
            #gr-print-root { display: none; }
            @media print {
              @page { size: 105mm 148mm; margin: 0; }
              body > *:not(#gr-print-root) { display: none !important; }
              #gr-print-root { display: block !important; }
              #gr-print-root .gr-print-card {
                position: relative; width: 105mm; height: 148mm; overflow: hidden;
                -webkit-print-color-adjust: exact; print-color-adjust: exact;
              }
            }
          `}</style>
          <PrintCard className="gr-print-card">{fundo && conteudoCartao}</PrintCard>
        </div>,
        document.body,
      )}
    </Shell>
  );
}

export default PresencaDigitalTab;

// ============================ estilos ============================
const Shell = styled.section`
  display: flex; flex-direction: column; gap: 18px;
`;
const Cabecalho = styled.div``;
const Titulo = styled.h2`
  margin: 0; font-family: var(--font-sans); font-size: 22px; line-height: 28px; font-weight: 500; color: #1F1F1F;
`;
const Legenda = styled.p`margin: 4px 0 0; font-size: 13px; line-height: 18px; color: #5f6368;`;
/* Prancheta PerfilPresencaDigital: controle segmentado no cabeçalho. */
const Seg = styled.div`
  display: inline-flex; border: 1px solid #E2E6EA; border-radius: 10px; overflow: hidden; background: #fff;
  button {
    height: 38px; padding: 0 14px; white-space: nowrap; border: 0; background: #fff;
    font-family: var(--font-sans); font-size: 13px; font-weight: 500; color: #5F6368; cursor: pointer;
    display: inline-flex; align-items: center; gap: 6px;
  }
  button + button { border-left: 1px solid #E2E6EA; }
  button.on { background: #EAF1FB; color: #0B4FA8; }
  button:disabled { opacity: .5; cursor: default; }
  .material-icons { font-size: 18px; }
`;

const Grade = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 18px;
`;
const CardProduto = styled.div<{ $ativo: boolean }>`
  background: #fff; border: 1px solid var(--line); border-radius: 16px;
  padding: 22px; display: flex; flex-direction: column; gap: 14px;
  cursor: ${(p) => (p.$ativo ? "pointer" : "default")};
  opacity: ${(p) => (p.$ativo ? 1 : 0.75)};
  transition: box-shadow .2s ease, transform .2s ease;
  &:hover { box-shadow: ${(p) => (p.$ativo ? "0 8px 24px rgba(60,64,67,.10)" : "none")}; transform: ${(p) => (p.$ativo ? "translateY(-2px)" : "none")}; }
`;
const CardTopo = styled.div`display: flex; gap: 13px; align-items: flex-start;`;
const IconeQuadrado = styled.div<{ $bg: string; $cor: string }>`
  width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
  background: ${(p) => p.$bg}; color: ${(p) => p.$cor};
  display: flex; align-items: center; justify-content: center;
  .material-icons { font-size: 23px; }
`;
const CardNome = styled.div`font-family: var(--font-sans); font-size: 16px; font-weight: 600; color: #1F1F1F;`;
const CardDesc = styled.div`font-size: 13px; color: #5f6368; line-height: 19px; margin-top: 3px;`;

const Mockup = styled.div`
  height: 170px; border-radius: 12px; background: #1F1F1F;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px;
`;
const MockEstrelas = styled.div`color: #F9AB00; font-size: 14px; letter-spacing: 1px;`;
const MockFrase = styled.div`color: #fff; font-size: 13.5px; font-weight: 500;`;
const MockQr = styled.div`
  width: 54px; height: 54px; border-radius: 6px; background: #fff;
  display: flex; align-items: center; justify-content: center; overflow: hidden;
  img { image-rendering: pixelated; }
`;
const MockSelo = styled.div`
  display: flex; align-items: center; gap: 6px; background: #c9a24b; color: #1F1F1F;
  padding: 5px 13px; border-radius: 999px; font-size: 11px; font-weight: 500;
`;
const MockG = styled.span`
  width: 14px; height: 14px; border-radius: 50%; background: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 9px; font-weight: 700; color: #1264C7;
`;
const MockupClaro = styled.div`
  height: 170px; border-radius: 12px; background: #F7F9FC; border: 1px solid var(--line);
  display: flex; align-items: center; justify-content: center;
`;
const MockJanela = styled.div`
  width: 150px; background: #fff; border: 1px solid #e5e7eb; border-radius: 8px;
  padding: 10px 12px 14px; display: flex; flex-direction: column; gap: 7px;
`;
const MockBolinhas = styled.div`
  display: flex; gap: 4px; margin-bottom: 3px;
  i { width: 6px; height: 6px; border-radius: 50%; background: #E2E6EA; }
`;
const MockLinha = styled.div`height: 7px; border-radius: 4px; background: #E2E6EA;`;
const MockBotaoFake = styled.div`
  height: 18px; border-radius: 5px; background: #1264C7; opacity: .85; margin-top: 3px;
`;

const Tags = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;
const Tag = styled.span`
  display: inline-flex; align-items: center; height: 24px; padding: 0 10px; border-radius: 999px;
  font-family: var(--font-sans); font-size: 12px; font-weight: 500; color: #5F6368; background: #F3F6FA;
`;
const Cta = styled.div`
  display: inline-flex; align-items: center; gap: 8px; align-self: flex-start;
  background: #1264C7; color: #fff; border-radius: 12px; height: 44px; padding: 0 18px;
  font-family: var(--font-sans); font-size: 14px; font-weight: 500;
`;
const AvisoCard = styled.div`
  font-size: 12.5px; color: #7a4a00; background: #FFF7E0;
  border: 1px solid #fde293; border-radius: 12px; padding: 12px 14px;
`;

// --- editor ---
const BarraEditor = styled.div`display: flex; align-items: center; gap: 14px;`;
const BotaoVoltar = styled.button`
  display: inline-flex; align-items: center; gap: 5px;
  border: 1px solid #E2E6EA; background: #fff; color: #0B4FA8; height: 36px;
  border-radius: 10px; padding: 0 12px; font-family: var(--font-sans); font-size: 13px; font-weight: 500; cursor: pointer;
`;
const TituloEditor = styled.h2`margin: 0; flex: 1; font-family: var(--font-sans); font-size: 22px; font-weight: 500; color: #1F1F1F;`;
const BotaoImprimir = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  background: #1264C7; border: none; color: #fff; border-radius: 12px; height: 44px;
  padding: 0 18px; font-family: var(--font-sans); font-size: 14px; font-weight: 500; cursor: pointer;
  &:disabled { opacity: .5; cursor: default; }
`;
const EditorGrade = styled.div`
  display: grid; grid-template-columns: minmax(250px, 340px) 1fr; gap: 22px;
  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;
const PreviewColuna = styled.div`display: flex; flex-direction: column; gap: 8px;`;
const CartaoPreview = styled.div`
  position: relative; width: 100%; aspect-ratio: 1060 / 1484;
  border-radius: 8px; overflow: hidden; background: #f1f3f4;
  border: 1px solid #e5e7eb; display: flex;
`;
const PreviewLegenda = styled.p`margin: 0; font-size: 12px; color: #5f6368;`;
const CartaoImg = styled.img`
  position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover;
`;
const CartaoCentro = styled.div<{ $escuro: boolean }>`
  position: absolute; inset: 0; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 3.2%; padding: 10%;
  text-align: center; color: ${(p) => (p.$escuro ? "#fff" : "#1F1F1F")};
`;
const CartaoEstrelas = styled.div`color: #F9AB00; font-size: 1.2em; letter-spacing: 2px;`;
const CartaoHeadline = styled.div`font-size: 1.35em; font-weight: 700; line-height: 1.25;`;
const CartaoSub = styled.div`font-size: .85em; opacity: .85;`;
const CartaoQrBox = styled.div`
  width: 38%; aspect-ratio: 1; background: #fff; border-radius: 7px;
  display: flex; align-items: center; justify-content: center; overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,.18);
  img { width: 92%; height: 92%; image-rendering: pixelated; }
`;
const CartaoNome = styled.div`font-size: .95em; font-weight: 600;`;
const CartaoSelo = styled.div`
  display: inline-flex; align-items: center; gap: 6px; background: #c9a24b; color: #1F1F1F;
  padding: 5px 14px; border-radius: 999px; font-size: .72em; font-weight: 600;
`;
const QrPendente = styled.div`font-size: 12px; color: #9aa0a6;`;

const Controles = styled.div`display: flex; flex-direction: column; gap: 14px; min-width: 0;`;
const AbasFundo = styled.div`display: flex; gap: 8px;`;
const AbaFundo = styled.button<{ $ativa: boolean }>`
  border: 1px solid ${(p) => (p.$ativa ? "#1264C7" : "#E2E6EA")};
  background: ${(p) => (p.$ativa ? "#EAF1FB" : "#fff")};
  color: ${(p) => (p.$ativa ? "#0B4FA8" : "#2B2D31")};
  border-radius: 999px; padding: 7px 14px; font-size: 12.5px; font-weight: 600; cursor: pointer;
`;
const Miniaturas = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  gap: 8px; max-height: 300px; overflow-y: auto; padding-right: 4px;
`;
const Miniatura = styled.button<{ $selecionada: boolean }>`
  position: relative; aspect-ratio: 1060 / 1484; padding: 0; overflow: hidden;
  border-radius: 6px; cursor: pointer; background: #f1f3f4;
  border: 2px solid ${(p) => (p.$selecionada ? "#1264C7" : "transparent")};
  outline: 1px solid #e5e7eb;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;
const Campo = styled.div``;
const Rotulo = styled.label`
  display: block; font-size: 12px; font-weight: 600; color: #5f6368; margin-bottom: 4px;
`;
const Entrada = styled.input`
  width: 100%; border: 1px solid #E2E6EA; border-radius: 6px;
  padding: 9px 11px; font-size: 13.5px; color: #1F1F1F;
`;
const Select = styled.select`
  width: 100%; border: 1px solid #E2E6EA; border-radius: 6px;
  padding: 9px 10px; font-size: 13px; color: #1F1F1F; background: #fff;
`;
const LinkInfo = styled.div``;
const LinkTexto = styled.p`
  margin: 0; font-size: 11.5px; color: #1F1F1F; word-break: break-all;
  background: #F7F9FC; border-radius: 6px; padding: 8px 10px;
`;
const PrintCard = styled.div`
  position: relative; font-size: 16pt;
`;

// --- editor do mini-site ---
